import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listConferenceRooms, getConferenceRoomAvailability } from 'services/Settings';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { combineDateAndTime } from 'utils/dateTimeUtils';

export const useConferenceRoomAvailability = (
  selectedLocation,
  meetingStartTime,
  meetingEndTime,
  options = {},
  excludeEventId = null,
  selectedDate = null
) => {
  const [conferenceRooms, setConferenceRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState(null);
  const { userDetails } = useUserDetailsContext();

  // Options to control behavior
  const {
    autoCheckAvailability = false, // Only check availability automatically if explicitly enabled
    checkOnTimeChange = false, // Only check when time changes if explicitly enabled
    isAllDay = false, // Whether the event is all-day
  } = options;

  // Fetch conference rooms for the selected location
  const fetchConferenceRooms = useCallback(async () => {
    if (!selectedLocation) {
      setConferenceRooms([]);
      return;
    }

    setIsLoading(true);
    setRoomsError(null);

    try {
      const roomsResponse = await listConferenceRooms(selectedLocation);

      const rooms = roomsResponse?.data || [];

      const filteredRooms = rooms
        .filter((room) => String(room?.office_location_id) === String(selectedLocation))
        .map((room) => ({
          ...room,
          label: room?.room_name || room?.name,
          value: room?.conference_room_id || room?.room_id || room?.id,
        }));

      setConferenceRooms(filteredRooms);
    } catch (err) {
      console.error('Error fetching conference rooms:', err);
      setRoomsError('Failed to load conference rooms');
      setConferenceRooms([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocation]);

  // Fetch rooms when location changes
  useEffect(() => {
    fetchConferenceRooms();
  }, [fetchConferenceRooms]);

  // Compute stable, serializable time strings for the query key so React Query
  // can correctly detect when to re-fetch (Date objects would always be new refs).
  const { formattedStartTime, formattedEndTime } = useMemo(
    () =>
      combineDateAndTime({
        selectedDate,
        meetingStartTime,
        meetingEndTime,
        isAllDay,
        timezone: userDetails?.timezone,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDate, String(meetingStartTime), String(meetingEndTime), isAllDay, userDetails?.timezone]
  );

  const shouldCheck =
    Boolean(autoCheckAvailability || checkOnTimeChange) && Boolean(selectedLocation && meetingStartTime && meetingEndTime);

  // useQuery replaces the manual useCallback + two useEffect pattern.
  // React Query guarantees only the response for the currently active query key
  // is committed to state, eliminating the race condition where a stale request
  // (e.g. one fired before excludeEventId was populated) could overwrite a
  // newer, correct response.
  const {
    data: rawAvailability,
    isFetching: isCheckingAvailability,
    error: availabilityError,
    refetch: refreshAvailability,
  } = useQuery({
    queryKey: ['conferenceRoomAvailability', selectedLocation, formattedStartTime, formattedEndTime, excludeEventId ?? null],
    queryFn: async () => {
      const payload = {
        office_location_id: selectedLocation,
        meeting_start_time: formattedStartTime,
        meeting_end_time: formattedEndTime,
        ...(excludeEventId && { exclude_event_id: excludeEventId }),
      };
      const response = await getConferenceRoomAvailability(payload);
      return response?.data || {};
    },
    enabled: shouldCheck,
    staleTime: 0,
    cacheTime: 0,
    retry: false,
    onError: (err) => console.error('Error checking room availability:', err),
  });

  // Derive availability map from query data
  const roomAvailability = useMemo(() => {
    if (!rawAvailability) return {};
    const map = {};
    Object.keys(rawAvailability).forEach((roomId) => {
      map[roomId] = rawAvailability[roomId]?.available === true;
    });
    return map;
  }, [rawAvailability]);

  // Generate room options with availability status - memoized to prevent infinite loops
  const roomOptionsWithAvailability = useMemo(() => {
    return conferenceRooms.map((room) => {
      const isAvailable = roomAvailability[room.value];
      const hasAvailabilityData = roomAvailability.hasOwnProperty(room.value);

      const capacityText = room.capacity ? ` (${room.capacity})` : '';

      // Show availability status in dropdown
      let availabilityText = '';
      if (hasAvailabilityData) {
        availabilityText = isAvailable ? ' (Available)' : ' (Unavailable)';
      } else {
        availabilityText = ' (Checking...)';
      }

      return {
        ...room,
        label: `${room.label}${capacityText}`, // Clean label for selected value display
        dropdownLabel: `${room.label}${capacityText}${availabilityText}`, // Full label with availability for dropdown
        isAvailable: isAvailable,
        disabled: hasAvailabilityData && isAvailable === false, // Disable unavailable rooms
        hasAvailabilityData: hasAvailabilityData,
      };
    });
  }, [conferenceRooms, roomAvailability]);

  return {
    conferenceRooms: roomOptionsWithAvailability,
    isLoading: isLoading || isCheckingAvailability,
    isCheckingAvailability,
    error: roomsError || (availabilityError ? 'Failed to check room availability' : null),
    refreshAvailability,
  };
};
